import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";

import { api, ApiError } from "../api/client";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Presence } from "../components/presence";
import { Button } from "../components/ui";
import { LicenceFrame } from "./ActivationScreen";
import { LICENCE_QUERY } from "./licence";
import { k, t } from "../i18n/i18n";

/** Notice de protection des données, reprise de `PrivacyConsentView` de l'iPad (version 1, juin 2026).
 *  Adaptée au web là où l'iPad ne dit plus vrai : l'identifiant du poste est un cookie du navigateur, et les
 *  dossiers d'exercice sont enregistrés sur le serveur Generex Web (ils restaient sur l'iPad). La section
 *  « Achat via l'App Store » est sans objet sur le web. */
const SECTIONS: [string, string[]][] = [
  [
    k("1. Responsable du traitement"),
    [
      k("Les traitements de données décrits ci-dessous sont mis en œuvre par BELLICOM, éditeur de Generex (« nous »). Contact : contact@bellicom.fr."),
    ],
  ],
  [
    k("2. Données traitées"),
    [
      k("Données de licence : un identifiant technique du poste (généré par Generex Web et conservé dans un cookie technique de votre navigateur — ni numéro de série, ni adresse matérielle, ni identifiant personnel), le nom du poste, le code de licence, et les horodatages d'activation et de vérification."),
      k("Journal technique du serveur de licences : événements d'activation, de désactivation et de contrôle, sans aucun contenu opérationnel."),
      k("Données opérationnelles (dossiers d'exercice, paramètres de mission, déplacements d'unités) : elles sont enregistrées sur le serveur Generex Web pour que vous puissiez les retrouver. Les questions posées à l'assistant IA sont traitées par un modèle exploité par BELLICOM, sans service tiers."),
    ],
  ],
  [
    k("3. Finalités et bases légales"),
    [
      k("Les données de licence sont traitées pour activer Generex, appliquer le nombre de terminaux autorisés par votre licence, assurer la sécurité du service (prévention de la fraude) et fournir le support. Les données opérationnelles sont traitées pour fournir le service. Ces traitements sont fondés sur l'exécution du contrat de licence (article 6.1.b du RGPD) et sur notre intérêt légitime à sécuriser le service (article 6.1.f)."),
    ],
  ],
  [
    k("4. Hébergement et transferts"),
    [
      k("Le serveur de licences et Generex Web sont hébergés dans l'Union européenne (OVHcloud, France). Aucune donnée n'est transférée hors de l'Union européenne, vendue, partagée à des fins publicitaires, ni utilisée à des fins de pistage. Generex ne contient ni publicité ni traceur."),
    ],
  ],
  [
    k("5. Durées de conservation"),
    [
      k("Les données de licence sont conservées pendant la durée de validité de la licence. Les journaux techniques sont conservés au maximum douze (12) mois après l'expiration de la licence, puis supprimés ou anonymisés. Les dossiers d'exercice sont conservés jusqu'à leur suppression par vos soins ou la fin du contrat."),
    ],
  ],
  [
    k("6. Vos droits"),
    [
      k("Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité sur vos données, que vous pouvez exercer en écrivant à contact@bellicom.fr. Vous pouvez également introduire une réclamation auprès de la CNIL (cnil.fr)."),
    ],
  ],
];

/** Version 2 (comptes nominatifs, docs/17 §8.3 et question 2, défauts d'Alexis du 03/10/2026) : données des comptes,
 *  ce que voient les administrateurs (décision 1 : jamais les dossiers privés d'un compte actif), durées de
 *  conservation du journal de l'unité et des sessions (purge nocturne lancée par la sauvegarde), sauvegardes gardées
 *  sur le serveur sans purge, copie de commodité décrite sans rien affirmer de son chiffrement (non vérifié). */
const ACCOUNT_DATA = k(
  "Comptes nominatifs : l'identifiant, le grade et nom, la fonction facultative et le rôle de chaque compte, l'empreinte de son mot de passe (jamais le mot de passe lui-même), ses sessions (poste, adresse IP, type de navigateur, heures de connexion et de dernière activité) et le journal de l'unité (connexions, codes remis, gestes d'administration, partages et transmissions, avec le nom des dossiers concernés mais jamais leur contenu). Aucune adresse électronique, aucun numéro de téléphone, aucun matricule. Le grade et nom d'un compte signe ses actions dans les journaux d'exercice (DIREX, ordres reçus, transmissions) et reste visible de la licence destinataire d'un dossier transmis. Les administrateurs de l'unité lisent le journal de l'unité ; ils voient le nom, la taille et la dernière activité des dossiers privés d'un compte, jamais leur contenu, et ne les ouvrent pas tant que le compte est actif. Un code de réinitialisation émis pour votre compte par un administrateur est journalisé et vous est annoncé à votre connexion suivante.",
);
const ACCOUNT_RETENTION = k(
  "Le journal de l'unité est conservé douze (12) mois, puis purgé automatiquement ; les sessions sont supprimées trente (30) jours après leur fin. Un compte désactivé est gardé pour l'attribution des actions passées et anonymisé sur demande adressée à BELLICOM ; les noms déjà écrits dans les journaux d'exercice restent, car ils font partie du compte rendu de l'exercice. Les sauvegardes de la base sont gardées sur le serveur Generex Web, sans être effacées d'office : elles conservent ces données au-delà de ces durées. Une copie de commodité de ces sauvegardes est aussi faite chaque jour sur un poste d'exploitation de BELLICOM, dans un dossier réservé au compte de son utilisateur.",
);

/** Version 3 du poste (administration des licences, demande d'Alexis du 02/11/2026) : la personne qui utilise le poste
 *  (nom, prénom, grade, service) et l'usage de Generex, ce qui en est fait, par qui, combien de temps. La version 2
 *  reste celle des comptes nominatifs (acceptée par chaque compte). */
const SEAT_DATA = k(
  "Personne qui utilise le poste : nom et prénom, grade et service facultatifs, indiqués à l'écran « Qui utilise ce poste ? » et modifiables dans Réglages › Ce poste ; l'unité est celle de la licence, indiquée par BELLICOM. Usage de Generex : vues ouvertes et temps passé dans chacune, nombre d'exports, de questions à l'assistant IA et de dossiers créés, dernière activité, présence en ce moment, type d'appareil et de navigateur. Jamais le contenu des dossiers ni de vos saisies.",
);
const SEAT_PURPOSE = k(
  "La personne déclarée et l'usage servent à BELLICOM à gérer les licences (savoir qui occupe chaque place, libérer un poste qui ne sert plus, rattacher les postes à leur unité) et à vous assister. Seule l'équipe de BELLICOM les consulte, depuis son outil d'administration des licences ; ils ne sont ni vendus, ni partagés, ni employés pour évaluer les personnes. Ces traitements sont fondés sur l'exécution du contrat de licence et sur notre intérêt légitime à gérer et sécuriser le service.",
);
const SEAT_RETENTION = k(
  "La personne déclarée est conservée tant que le poste occupe une place de la licence, puis effacée quand il est libéré. Le détail de l'usage est conservé quatre-vingt-dix (90) jours, ses totaux par jour vingt-quatre (24) mois, puis supprimés automatiquement.",
);

/** Version 3 d'un compte (décision du 02/11/2026) : l'usage suivi, comme pour le poste, avec ses durées. */
const ACCOUNT_USAGE = k(
  "Usage de Generex par chaque compte : vues ouvertes et temps passé dans chacune, nombre d'exports, de questions à l'assistant IA et de dossiers créés, dernière activité, présence en ce moment, type d'appareil et de navigateur. Jamais le contenu des dossiers ni de vos saisies.",
);
const ACCOUNT_USAGE_PURPOSE = k(
  "L'usage sert à BELLICOM à gérer les licences et à vous assister. Seule l'équipe de BELLICOM le consulte, depuis son outil d'administration des licences ; il n'est ni vendu, ni partagé, ni employé pour évaluer les personnes.",
);
const ACCOUNT_USAGE_RETENTION = k(
  "Le détail de l'usage est conservé quatre-vingt-dix (90) jours, ses totaux par jour vingt-quatre (24) mois, puis supprimés automatiquement.",
);

/** Sections de la notice. Notice d'un compte (`accounts`) : version 2, comptes nominatifs ; version 3, avec l'usage
 *  suivi. Notice du poste : version 3, avec la personne qui utilise le poste et l'usage. Sans précision, la version 2
 *  est celle d'un compte (seule à l'avoir été). */
function sectionsOf(version: number, accounts: boolean): [string, string[]][] {
  if (version < 2) return SECTIONS;
  const extra: (string | null)[][] = accounts
    ? [[ACCOUNT_DATA, ...(version >= 3 ? [ACCOUNT_USAGE] : [])], version >= 3 ? [ACCOUNT_USAGE_PURPOSE] : [],
       [ACCOUNT_RETENTION, ...(version >= 3 ? [ACCOUNT_USAGE_RETENTION] : [])]]
    : [[SEAT_DATA], [SEAT_PURPOSE], [SEAT_RETENTION]];
  return SECTIONS.map(([title, paragraphs], index) => {
    const added = (index === 1 ? extra[0] : index === 2 ? extra[1] : index === 4 ? extra[2] : undefined) ?? [];
    return [title, [...paragraphs, ...added.filter((text): text is string => Boolean(text))]];
  });
}

/** Texte de la notice (défilant), aussi montré seul depuis l'écran du compte administrateur. */
export function NoticeText({ version, className, accounts }: { version: number; className?: string; accounts?: boolean }) {
  return (
    <div className={className ?? "max-h-[46vh] space-y-3.5 overflow-y-auto pr-1"}>
      {sectionsOf(version, accounts ?? version === 2).map(([title, paragraphs]) => (
        <section key={title}>
          <h2 className="text-ui font-semibold text-ink">{t(title)}</h2>
          {paragraphs.map((text) => (
            <p key={text.slice(0, 24)} className="mt-1 text-ui leading-relaxed text-ink-soft">
              {paragraphs.length > 1 ? "• " : ""}
              {t(text)}
            </p>
          ))}
        </section>
      ))}
      <p className="text-meta text-ink-muted">
        {version >= 3
          ? t("Version {version} (web) — novembre 2026. Politique complète disponible sur api.bellicom.fr/confidentialite.", { version })
          : version >= 2
          ? t("Version {version} (web) — octobre 2026. Politique complète disponible sur api.bellicom.fr/confidentialite.", { version })
          : t("Version {version} (web) — septembre 2026. Politique complète disponible sur api.bellicom.fr/confidentialite.", { version })}
      </p>
    </div>
  );
}

/** Écran bloquant après chaque activation : case à cocher puis « Accepter et continuer ». `onAccept` : notice d'un
 *  compte (comptes nominatifs, version 2, acceptée par la personne elle-même à sa première connexion) au lieu de
 *  celle du poste. */
export function PrivacyConsent({
  version,
  kept = false,
  onAccept,
}: {
  version: number;
  kept?: boolean;
  onAccept?: () => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const [ticked, setTicked] = useState(false);
  const accept = useMutation({
    mutationFn: () => (onAccept ? onAccept() : api.acceptPrivacy()),
    onSuccess: (next) => {
      if (onAccept) return;
      queryClient.setQueryData(LICENCE_QUERY, next);
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== LICENCE_QUERY[0] });
    },
  });
  return (
    <LicenceFrame wide kept={kept}>
      <h1 className="flex items-center gap-2 text-title font-semibold text-ink">
        <ShieldCheck size={16} className="text-ink-muted" /> {t("Protection des données personnelles")}
      </h1>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">
        {t("Information conforme au Règlement (UE) 2016/679 (« RGPD ») — à lire avant d'utiliser Generex.")}
      </p>

      <NoticeText version={version} accounts={Boolean(onAccept)} className="mt-4 max-h-[46vh] space-y-3.5 overflow-y-auto pr-1" />

      <div className="mt-4 space-y-3 border-t border-line pt-4">
        <Checkbox
          data-attention="privacy-consent"
          checked={ticked}
          onChange={setTicked}
          label={t("J'ai lu et j'accepte les conditions de traitement des données personnelles ci-dessus.")}
        />
        <Presence show={Boolean(accept.error)}>
          {accept.error && (
            <Callout tone="danger">
              {accept.error instanceof ApiError ? accept.error.message : t("L'acceptation n'a pas pu être enregistrée.")}
            </Callout>
          )}
        </Presence>
        {/* Case non cochée (R12) : le bouton reste cliquable et désigne la case. */}
        <Button
          className="w-full"
          disabled={accept.isPending}
          blocked={!ticked}
          onBlocked={() => designate(attentionSelector("privacy-consent"))}
          onClick={() => accept.mutate()}
        >
          {t("Accepter et continuer")}
        </Button>
        <p className="text-meta text-ink-muted">
          {onAccept ? t("L'acceptation (version et date) est enregistrée pour votre compte.") : t("L'acceptation (version et date) est enregistrée pour ce poste.")}
        </p>
      </div>
    </LicenceFrame>
  );
}
